import { GripVertical, MoveHorizontal, cn } from "@app/ui";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { animate, motion, springs, useMotionValue, useReducedMotion } from "../../lib/motion/index.js";
import { useI18n } from "../../lib/useI18n.js";
import type { TodayModuleId } from "./layout.js";

const DRAG_START_PX = 4;

export function TodayModuleShell({
  id, left, top, width, measured, dragging, spanState, customizable,
  onHeight, onCycleSpan, onKeyboardMove, onDragStart, onDragMove, onDrop, onCancel, children,
}: {
  id: TodayModuleId;
  left: number;
  top: number;
  width: number;
  /** False until the first height measurement — rendered invisible so the
   *  pre-pack frame never flashes at the wrong spot. */
  measured: boolean;
  dragging: boolean;
  spanState: { span: number; cols: number };
  customizable: boolean;
  onHeight: (h: number) => void;
  onCycleSpan: () => void;
  onKeyboardMove: (key: "ArrowLeft" | "ArrowRight" | "ArrowUp" | "ArrowDown") => void;
  onDragStart: () => void;
  onDragMove: (clientX: number, clientY: number) => void;
  onDrop: (clientX: number, clientY: number) => void;
  onCancel: () => void;
  children: React.ReactNode;
}) {
  const { t } = useI18n();
  const reduce = useReducedMotion();
  const shellRef = useRef<HTMLDivElement | null>(null);
  // ALL module motion rides these two values. The drag is PLAIN pointer
  // events (capture on the grip → clientX/Y deltas written straight into
  // x/y), and the FLIP effect below springs them back to 0 after any slot
  // change. Framer's drag gesture and `layout` projection are deliberately
  // NOT used in this grid: two systems sharing one transform is what caused
  // every release glitch (overshoot, stale-snapshot jumps, stranded modules).
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  /** Keeps the z-boost + shadow through the post-release glide. */
  const [lifted, setLifted] = useState(false);
  /** Invalidates completion callbacks from a superseded settle. A second
   * layout change can retarget the same motion values before the first pair of
   * animations resolves; only the latest pair may lower the module. */
  const settleGenerationRef = useRef(0);
  const slotRef = useRef<{ left: number; top: number } | null>(null);
  const releasedRef = useRef(false);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  /** Armed/active grip gesture. A tap stays armed=false and never commits. */
  const gripRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    baseX: number;
    baseY: number;
    started: boolean;
  } | null>(null);

  // Measured height feeds the skyline pack; offsetHeight ignores transforms,
  // so drag scale / parting can't perturb the layout. Measured after EVERY
  // commit on purpose: content growth (store hydration filling a day card, a
  // width change re-wrapping text) flows through React, so this catches it
  // synchronously — ResizeObserver callbacks are frame-scheduled and would
  // pack a stale height for a frame (or longer in a throttled tab). The
  // equality guard in onHeight keeps this loop-free.
  useLayoutEffect(() => {
    const el = shellRef.current;
    if (el) onHeight(Math.round(el.offsetHeight));
  });

  // ResizeObserver for height changes React never sees (fonts, images).
  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => onHeight(Math.round(el.offsetHeight)));
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A sync/update can remove a conditional module while its grip owns pointer
  // capture. Clear the parent drag state on unmount so the grid cannot remain
  // stranded in its preview/lift state without a grip left to receive cancel.
  useEffect(() => () => {
    if (!gripRef.current?.started) {
      gripRef.current = null;
      return;
    }
    gripRef.current = null;
    onCancelRef.current();
  }, []);

  // HAND-ROLLED FLIP — the one system that moves modules. Whenever the packed
  // slot (left/top, incl. the parting nudge) changes, rebase the current
  // offset so the visual stays put (x += oldSlot − newSlot), then spring it
  // to 0. A release is the same move with the drag offset as the starting
  // point, so the module glides from exactly where it was let go into its new
  // slot. This runs in a layoutEffect, so between release and settle there is
  // never a painted frame at the wrong position. While dragging, the pointer
  // owns the offset: the rebase still applies (a mid-drag repack must not
  // teleport the card) and the grip's base shifts with it, but nothing animates.
  useLayoutEffect(() => {
    const prev = slotRef.current;
    slotRef.current = { left, top };
    const moved = !!prev && (prev.left !== left || prev.top !== top);
    if (moved) {
      x.set(x.get() + prev.left - left);
      y.set(y.get() + prev.top - top);
      const grip = gripRef.current;
      if (grip) {
        grip.baseX += prev.left - left;
        grip.baseY += prev.top - top;
      }
    }
    const released = releasedRef.current;
    releasedRef.current = false;
    // A pointer-down that has not crossed the drag threshold does not own the
    // motion values. Let responsive/content repacks keep settling underneath
    // that inert tap; otherwise a rebase during the armed phase can be left
    // stranded when pointer-up correctly declines to start a drag.
    if (dragging || gripRef.current?.started) return;
    if (!moved && !released) return; // leave an in-flight settle alone
    if (x.get() === 0 && y.get() === 0) {
      settleGenerationRef.current++;
      setLifted(false);
      return;
    }
    if (reduce) {
      settleGenerationRef.current++;
      x.set(0);
      y.set(0);
      setLifted(false);
      return;
    }
    // velocity: 0 — the settle always heads straight for the slot; inheriting
    // the throw velocity carried the card past it for a beat on release.
    const spring = { type: "spring", stiffness: 420, damping: 41, velocity: 0 } as const;
    const generation = ++settleGenerationRef.current;
    const ax = animate(x, 0, spring);
    const ay = animate(y, 0, spring);
    void Promise.all([ax, ay]).then(() => {
      if (settleGenerationRef.current === generation) setLifted(false);
    });
  });

  // ——— The drag itself: plain captured pointer events on the grip. ———
  function gripDown(e: React.PointerEvent<HTMLButtonElement>) {
    if (gripRef.current) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.preventDefault();
    gripRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      baseX: x.get(),
      baseY: y.get(),
      started: false,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch { /* already-released pointer — the move/up guards still match by id */ }
  }

  function gripMove(e: React.PointerEvent<HTMLButtonElement>) {
    const grip = gripRef.current;
    if (!grip || e.pointerId !== grip.pointerId) return;
    const dx = e.clientX - grip.startX;
    const dy = e.clientY - grip.startY;
    if (!grip.started) {
      if (Math.hypot(dx, dy) < DRAG_START_PX) return;
      grip.started = true;
      // Do not interrupt an existing settle on pointer-down: a tap on the grip
      // must be inert. Take over the live offsets only once this is a drag.
      settleGenerationRef.current++;
      x.stop();
      y.stop();
      grip.baseX = x.get();
      grip.baseY = y.get();
      setLifted(true);
      onDragStart();
    }
    x.set(grip.baseX + dx);
    y.set(grip.baseY + dy);
    onDragMove(e.clientX, e.clientY);
  }

  function gripEnd(e: React.PointerEvent<HTMLButtonElement>) {
    const grip = gripRef.current;
    if (!grip || e.pointerId !== grip.pointerId) return;
    gripRef.current = null;
    if (!grip.started) return;
    releasedRef.current = true;
    // The parent clears `dragging` and commits the move in one batch; the
    // FLIP effect above turns the leftover offset into the settle glide.
    onDrop(e.clientX, e.clientY);
  }

  function gripCancel(e: React.PointerEvent<HTMLButtonElement>) {
    const grip = gripRef.current;
    if (!grip || e.pointerId !== grip.pointerId) return;
    gripRef.current = null;
    if (!grip.started) return;
    releasedRef.current = true;
    onCancel();
  }

  return (
    <motion.div
      ref={shellRef}
      data-today-module={id}
      initial={reduce ? false : { opacity: 0 }}
      // Scale + shadow are STATE-driven (not whileDrag) so the lift always
      // reverts on release, even if the gesture ends abnormally.
      animate={{
        opacity: measured ? 1 : 0,
        scale: dragging ? 0.96 : 1,
        boxShadow: dragging || lifted ? "0 12px 32px rgba(0,0,0,0.22)" : "0 0px 0px rgba(0,0,0,0)",
      }}
      transition={reduce ? { duration: 0 } : springs.smooth}
      className={cn(
        "group/module absolute [@media(hover:none)]:pt-3",
        (dragging || lifted) && "z-30",
        dragging && "cursor-grabbing",
      )}
      style={{ left, top, width, x, y }}
    >
      {customizable && <div
        className={cn(
          // Float in the inter-module gutter. Keeping the controls inside the
          // card's top-right corner intercepts hero/spotlight actions even
          // while this rail is visually transparent.
          "absolute -top-3 right-2 z-10 flex items-center gap-1 transition-opacity motion-reduce:transition-none",
          "opacity-0 focus-within:opacity-100 group-hover/module:opacity-100 [@media(hover:none)]:opacity-50",
          dragging && "opacity-100",
        )}
      >
        {spanState.cols > 1 && (
          <button
            type="button"
            aria-label={`${t("today.moduleWidth")} (${spanState.span}/${spanState.cols})`}
            title={`${t("today.moduleWidth")} (${spanState.span}/${spanState.cols})`}
            onClick={onCycleSpan}
            className="grid h-6 w-6 place-items-center rounded-md border bg-card text-muted-foreground shadow-sm hover:text-foreground"
          >
            <MoveHorizontal className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          aria-label={t("today.moveModule")}
          aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown"
          title={`${t("today.moveModule")} (\u2191 \u2193 \u2190 \u2192)`}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "ArrowUp" || e.key === "ArrowDown") {
              e.preventDefault();
              e.stopPropagation();
              onKeyboardMove(e.key);
            }
          }}
          onPointerDown={gripDown}
          onPointerMove={gripMove}
          onPointerUp={gripEnd}
          onPointerCancel={gripCancel}
          onLostPointerCapture={gripCancel}
          className="grid h-6 w-6 cursor-grab touch-none place-items-center rounded-md border bg-card text-muted-foreground shadow-sm hover:text-foreground active:cursor-grabbing"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      </div>}
      {children}
    </motion.div>
  );
}
